import { defineComponent, computed, ref, watch } from '@vue/composition-api';
import { BfSpinner, BfButton } from 'components/Butterfly';
import Icon from 'src/components/Icon.vue';
import CreateGroupModal from './CreateGroupModal.vue';
import GroupNameModal from './GroupNameModal.vue';
import GroupDeleteModal from './GroupDeleteModal.vue';
import { ThemeGroup } from 'src/api/query';
import { findNode, findParentNode } from './ThemeBuilder.utils';
import Dropdown from 'components/Butterfly/Dropdown/Dropdown.vue';
import DropdownItem from 'components/Butterfly/Dropdown/DropdownItem.vue';
// Map CoverageNode to TreeNode
export const mapGroupTreeToElTree = (coverageNodes) => {
    return coverageNodes.reduce((arr, node) => {
        if (node.is_new)
            return arr;
        const treeNode = {
            label: node.name,
            id: node.id,
            type: node.type,
            key: `${node.id}-${node.type}`,
        };
        if (node.children) {
            treeNode.children = mapGroupTreeToElTree(node.children);
        }
        return arr.concat(treeNode);
    }, []);
};
const ThemesTab = defineComponent({
    components: {
        GroupDeleteModal,
        GroupNameModal,
        CreateGroupModal,
        BfSpinner,
        BfButton,
        Icon,
        Dropdown,
        DropdownItem,
    },
    props: {
        isLoading: { type: Boolean, required: false, default: true },
        themes: { type: Array, required: false, default: () => [] },
        selectedThemeID: {
            type: [String, Number, null],
            default: null,
        },
        hasChanges: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        analysisId: { type: Number, required: true },
        projectId: { type: Number, required: true },
        groupStructure: { type: Array, required: true },
    },
    setup(props, { emit }) {
        const createGroupModalVisible = ref(false);
        const renameGroupId = ref(null);
        const deleteGroupId = ref(null);
        const renameErrors = ref([]);
        const dropTarget = ref(null);
        const updatingTree = ref(false);
        const draggingNode = ref(null);
        watch(() => props.groupStructure, () => {
            updatingTree.value = false;
        });
        const renameGroupName = computed(() => {
            if (renameGroupId.value !== null) {
                const node = findNode(props.groupStructure, { id: renameGroupId.value, type: 'group' });
                return node?.name;
            }
            return '';
        });
        const deleteGroupName = computed(() => {
            if (deleteGroupId.value !== null) {
                const node = findNode(props.groupStructure, { id: deleteGroupId.value, type: 'group' });
                return node?.name;
            }
            return '';
        });
        const groupUnsaved = (node) => {
            return node.children?.filter((child) => props.hasChanges[child.id])?.length ?? 0;
        };
        const getCoverage = (params) => {
            const node = findNode(props.groupStructure, { id: params.id, type: params.type });
            return node?.coverage ?? 0;
        };
        const getCoveragePercent = (params) => {
            return +(getCoverage(params) * 100).toFixed(2);
        };
        const countChildThemes = (node) => {
            if (!node.children)
                return 0;
            return node.children.reduce((acc, child) => {
                if (child.type === 'theme')
                    return acc + 1;
                return acc + countChildThemes(child);
            }, 0);
        };
        const savedThemes = computed(() => {
            return props.themes.filter((theme) => !theme.is_new);
        });
        const newThemes = computed(() => {
            return props.themes.filter((theme) => theme.is_new);
        });
        const convertedStructure = computed(() => {
            return mapGroupTreeToElTree(props.groupStructure);
        });
        const nodeClick = (data) => {
            if (data.type === 'theme') {
                emit('theme-clicked', data.id);
            }
        };
        const handleDrop = async (child, other) => {
            updatingTree.value = true;
            let parentId;
            if (other.data.type === 'theme') {
                // Dropped as a sibling of "other"
                const searchParams = { id: other.data.id, type: other.data.type };
                const parent = findParentNode(props.groupStructure, searchParams);
                parentId = parent?.id;
            }
            else {
                parentId = other.data.id;
            }
            // If dropped in Ungrouped themes, parentId is -1
            if (parentId === -1) {
                parentId = undefined;
            }
            const endpoint = child.data.type === 'theme'
                ? ThemeGroup.updateThemeParent
                : ThemeGroup.updateGroupParent;
            await endpoint(props.projectId, props.analysisId, child.data.id, parentId ?? null);
            emit('update-tree');
        };
        const renameGroupSubmit = async ({ name }) => {
            renameErrors.value = [];
            if (renameGroupId.value !== null) {
                const sameName = findNode(props.groupStructure, { name });
                if (sameName) {
                    renameErrors.value = [
                        `A ${sameName.type} with the name "${name}" already exists.`
                    ];
                    return;
                }
                try {
                    await ThemeGroup.update(props.projectId, props.analysisId, renameGroupId.value, { group_name: name });
                    renameGroupId.value = null;
                    emit('update-tree');
                }
                catch (e) {
                    console.error(e);
                }
            }
        };
        const deleteGroupSubmit = async () => {
            if (deleteGroupId.value !== null) {
                await ThemeGroup.delete(props.projectId, props.analysisId, deleteGroupId.value);
                deleteGroupId.value = null;
                emit('update-tree');
            }
        };
        const nodeDragEnter = (dragNode, dropNode) => {
            const currentParent = findParentNode(props.groupStructure, {
                id: dragNode.data.id,
                type: dragNode.data.type,
            });
            const data = dropNode.data;
            let dropTargetId = null;
            if (data.type === 'group') {
                dropTargetId = data.id;
            }
            else {
                const searchParams = { id: data.id, type: data.type };
                const parent = findParentNode(props.groupStructure, searchParams);
                if (parent) {
                    dropTargetId = parent.id;
                }
            }
            if (currentParent?.id !== dropTargetId) {
                dropTarget.value = dropTargetId;
            }
            else {
                dropTarget.value = null;
            }
        };
        const allowDrag = (node) => {
            return node.data.id !== -1;
        };
        const groupMenuToggle = (node, open) => {
            if (open)
                node.expanded = true;
        };
        return {
            renameErrors,
            renameGroupId,
            renameGroupName,
            renameGroupSubmit,
            deleteGroupId,
            deleteGroupName,
            deleteGroupSubmit,
            getCoveragePercent,
            savedThemes,
            newThemes,
            convertedStructure,
            createGroupModalVisible,
            nodeClick,
            handleDrop,
            groupUnsaved,
            countChildThemes,
            nodeDragEnter,
            dropTarget,
            updatingTree,
            draggingNode,
            allowDrag,
            groupMenuToggle,
        };
    }
});
export default ThemesTab;
